import PropTypes from 'prop-types';
import MultiselectGrouped from 'Components/Molecules/MultiselectGrouped/MultiselectGrouped';
import Chip from 'Components/Molecules/Chip/Chip';
import clsx from 'clsx';
import TagsList from 'Components/Molecules/TagsList/TagsList';
import { cn } from 'Utils/cn';
import { forwardRef } from 'react';

const MultiselectTagsMultipleTaxonomies = forwardRef(function MultiselectTagsMultipleTaxonomies(props, ref) {
    const { value, onChange, label, disabled, error, className, highlight, actions, categories, taxonomies } = props;

    const optionsFormatted = categories.map((category) => ({
        ...category,
        label: category.name,
        className: clsx(category.parent_tag_id && 'pl-5'),
    }));

    const categoriesByTaxonomy = taxonomies
        .map((taxonomy) => ({
            groupId: taxonomy.id,
            label: taxonomy.name,
            items: optionsFormatted.filter((category) => category.taxonomy_id === taxonomy.id),
        }))
        .filter((group) => group.items.length > 0);

    const optionsSelected = Array.isArray(value) ? value : [];
    const hasSelected = optionsSelected.length > 0;

    const handleChange = (selectedIds) => {
        const isAdding = selectedIds.length > optionsSelected.length;

        if (selectedIds.length === 0) {
            onChange([]);
            return;
        }

        if (isAdding) {
            const selectedWithParentsIds = selectedIds.reduce((acc, id) => {
                const category = categories.find((cat) => cat.id === id);
                if (category) {
                    acc.add(id);
                    if (category.parent_tag_id) {
                        const parentCategory = categories.find((cat) => cat.id === category.parent_tag_id);
                        if (parentCategory && !acc.has(parentCategory.id)) {
                            acc.add(parentCategory.id);
                        }
                    }
                }
                return acc;
            }, new Set());

            onChange(Array.from(selectedWithParentsIds));
        } else {
            const tagsRemoved = optionsSelected.filter((id) => !selectedIds.includes(id));
            const selectedWithoutOrphans = selectedIds.filter((id) => {
                const category = categories.find((cat) => cat.id === id);
                if (!category) return false;

                return !tagsRemoved.includes(category.parent_tag_id);
            });
            onChange(selectedWithoutOrphans);
        }
    };

    return (
        <MultiselectGrouped
            ref={ref}
            disabled={disabled}
            enableClear={hasSelected}
            onClear={() => onChange([])}
            floatLabel={label}
            label={<TagsList tagIds={optionsSelected} tags={categories} />}
            options={categoriesByTaxonomy}
            optionsSelected={optionsSelected}
            onChangeSelected={handleChange}
            className={cn('h-auto min-h-11 py-2', className)}
            error={error}
            highlight={highlight}
            renderOption={(option) => (
                <Chip key={option.id} color={option.color} icon={option.icon}>
                    {option.name}
                </Chip>
            )}
            actions={actions}
        />
    );
});

export default MultiselectTagsMultipleTaxonomies;

MultiselectTagsMultipleTaxonomies.propTypes = {
    disabled: PropTypes.bool,
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    className: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
    onAdd: PropTypes.func,
    actions: PropTypes.arrayOf(PropTypes.node),
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            parent_tag_id: PropTypes.string,
            color: PropTypes.string,
            icon: PropTypes.node,
        }),
    ).isRequired,
    taxonomies: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        }),
    ).isRequired,
};
